// Shared client-side filtering for the RI views so the Table and the Diagram
// narrow identically. objectId/direction are applied server-side (loadGraph
// returns a multi-hop closure for objectId), so the Diagram is passed only the
// client-only filters (type/relType/crossSchema) + search — see filterGraph callers.

export function matchesFilters(edge, filters) {
  if (filters.objectId && edge.parentObjectId !== filters.objectId && edge.childObjectId !== filters.objectId) {
    return false;
  }
  if (filters.relType && filters.relType !== 'all') {
    if ((edge.relType || 'FK') !== filters.relType) return false;
  }
  if (filters.direction && filters.direction !== 'both') {
    if (filters.direction === 'upstream' && !edge.isUpstream) return false;
    if (filters.direction === 'downstream' && !edge.isDownstream) return false;
  }
  if (filters.crossSchema === true && !edge.crossSchema) return false;
  return true;
}

export function matchesSearch(edge, search) {
  if (!search) return true;
  const q = search.toLowerCase();
  return (
    (edge.parentObjectName || '').toLowerCase().includes(q) ||
    (edge.childObjectName || '').toLowerCase().includes(q) ||
    (edge.constraintName || '').toLowerCase().includes(q)
  );
}

// Filter a graph by the active filters + search. Returns filtered edges and the
// nodes to display: when nothing is narrowing, all nodes (preserves isolated/
// edgeless nodes in the diagram); when narrowing, nodes are pruned to the
// endpoints of surviving edges plus any node whose name matches the search text.
export function filterGraph(graph, filters, search) {
  if (!graph) return {nodes: [], edges: []};
  const f = filters || {};
  const nodes0 = graph.nodes || [];

  // Real object types come from the nodes, not the edges — the Type filter needs
  // this map because edges only carry relType (FK/VIEW_DEP), never table/view.
  const typeById = new Map(nodes0.map((n) => [n.objectId || n.id, (n.objectType || 'table').toLowerCase()]));
  const passesType = (e) => {
    if (!f.type || f.type === 'all') return true;
    const src = typeById.get(e.parentObjectId || e.source);
    const tgt = typeById.get(e.childObjectId || e.target);
    return src === f.type || tgt === f.type;
  };

  const edges = (graph.edges || [])
      .filter((e) => matchesFilters(e, f))
      .filter(passesType)
      .filter((e) => matchesSearch(e, search));

  const active = !!search || !!f.objectId ||
      (f.type && f.type !== 'all') ||
      (f.relType && f.relType !== 'all') ||
      f.crossSchema === true ||
      (f.direction && f.direction !== 'both');

  if (!active) return {nodes: nodes0, edges};

  const keep = new Set();
  edges.forEach((e) => {
    keep.add(e.parentObjectId || e.source);
    keep.add(e.childObjectId || e.target);
  });
  const q = (search || '').toLowerCase();
  const nodes = nodes0.filter((n) => {
    const id = n.objectId || n.id;
    if (keep.has(id)) return true;
    if (q && (n.objectName || n.label || '').toLowerCase().includes(q)) return true;
    return false;
  });
  return {nodes, edges};
}
